import { ColumnDef } from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import ApprovalLevelTooltipIcon from '@/components/atoms/ApprovalLevelTooltipIcon';
import TableData from '@/components/atoms/TableData';
import ApprovalFlowButtons from '@/components/organisms/ApprovalFlowButtons';
import { ApprovalFlowTypeEnum } from '@/constants/enums/ApprovalFlowTypeEnum';
import { cn } from '@/utils/cn';

export interface ExtraTripsApprovalFlow {
	id: number;
	company: { id: number; name: string };
	board: { id: number; name: string };
	management: { id: number; name: string };
	type: string;
	requesters?: Array<{ employeeId: number; name: string }>;
	approvalSteps?: Array<{
		step: number;
		approvers: Array<{ employeeId: number; name: string }>;
	}>;
	subRows?: Array<{
		id: string;
		isSubRow: boolean;
		parentId: number;
		approvalSteps: Array<{
			step: number;
			approvers: Array<{ employeeId: number; name: string }>;
		}>;
	}>;
}

const isApprovalSubRow = (row: ExtraTripsApprovalFlow) =>
	Boolean((row as ExtraTripsApprovalFlow & { isSubRow?: boolean }).isSubRow);

const subRowColSpan =
	(span: number | 'afterCompany') =>
	({
		row,
		visibleColumnCount,
	}: {
		row: ExtraTripsApprovalFlow;
		visibleColumnCount: number;
	}) => {
		if (!isApprovalSubRow(row)) return 1;
		return span === 'afterCompany' ? Math.max(visibleColumnCount - 2, 1) : span;
	};

const ApproverNames = ({
	employees,
}: {
	employees: Array<{ employeeId: number; name: string }>;
}) => {
	const textRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);
	const names = employees.map((employee) => employee.name).join(', ');

	useEffect(() => {
		const element = textRef.current;
		if (!element) return;

		const checkTruncation = () => {
			setIsTruncated(
				names.length > 0 && element.scrollWidth > element.clientWidth,
			);
		};

		checkTruncation();
		const observer = new ResizeObserver(checkTruncation);
		observer.observe(element);
		return () => observer.disconnect();
	}, [names]);

	return (
		<>
			<span ref={textRef} className="inline-block min-w-0 flex-1 truncate">
				{names}
			</span>
			{isTruncated && <ApprovalLevelTooltipIcon employees={employees} />}
		</>
	);
};

const getTypeLabel = (type: string): string => {
	switch (type) {
		case ApprovalFlowTypeEnum.Emergency:
			return 'EMERGENCIAL';
		case ApprovalFlowTypeEnum.Regular:
			return 'REGULAR';
		default:
			return type;
	}
};

export const getColumns = (
	onEdit?: (approvalFlow: ExtraTripsApprovalFlow) => void,
): Array<ColumnDef<ExtraTripsApprovalFlow>> => [
	{
		accessorKey: 'expand',
		header: '',
		meta: {
			headerClassName: 'bg-[#F5F7FF]',
		},
		cell: (props) => {
			const row = props.row;
			const isSubRow = (row.original as any).isSubRow;
			const approvalFlow = row.original;
			const hasSteps =
				!isSubRow &&
				approvalFlow.approvalSteps &&
				approvalFlow.approvalSteps.length > 0;

			if (isSubRow || !hasSteps) {
				return <div className="h-12 w-10"></div>;
			}

			return (
				<div className="flex h-12 w-10 items-center justify-center">
					<button
						onClick={() => row.toggleExpanded()}
						type="button"
						className="flex h-full w-full items-center justify-center"
					>
						<FaChevronDown
							className={cn('h-3 text-[#193CB9] transition-transform', {
								'rotate-180': row.getIsExpanded(),
							})}
						/>
					</button>
				</div>
			);
		},
		enableSorting: false,
	},
	{
		accessorKey: 'company.name',
		header: 'Empresa',
		meta: {
			headerClassName: 'text-[#193CB9] bg-[#F5F7FF] pl-3',
			trClassname: 'border-t border-b border-[#E2E9FF] bg-white',
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				const subRow = props.row.original as any;
				const approvalSteps = subRow.approvalSteps || [];

				return (
					<div className="py-2 pl-3">
						<div className="mb-2 text-sm font-medium text-[#193CB9]">Nível</div>
						<div className="flex flex-col gap-2">
							{approvalSteps.map((step: any) => (
								<div
									key={`step-${step.step}`}
									className="flex h-9 items-center rounded-s-lg bg-[#F8FAFB] px-2 text-xs text-gray-700"
								>
									<span className="truncate">Nível {step.step}</span>
								</div>
							))}
						</div>
					</div>
				);
			}
			return (
				<TableData
					tableValue={props.getValue() || '-'}
					className="overflow-hidden text-ellipsis whitespace-nowrap px-3"
					title={props.getValue() as string}
				/>
			);
		},
	},
	{
		accessorKey: 'board.name',
		header: 'Diretoria',
		meta: {
			headerClassName:
				'text-[#193CB9] bg-[#F5F7FF] pl-3 border-l border-[#EAEFFF]',
			getCellColSpan: subRowColSpan('afterCompany'),
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				const subRow = props.row.original as any;
				const approvalSteps = subRow.approvalSteps || [];

				return (
					<div className="py-2">
						<div className="mb-2 pl-3 text-sm font-medium text-[#193CB9]">
							Colaboradores
						</div>
						<div className="flex flex-col gap-2">
							{approvalSteps.map((step: any) => (
								<div
									key={`step-${step.step}`}
									className="flex h-9 min-w-0 items-center gap-2 rounded-e-lg bg-[#F8FAFB] py-[2px] pl-3 pr-2 text-xs text-gray-700 overflow-hidden"
								>
									<ApproverNames employees={step.approvers || []} />
								</div>
							))}
						</div>
					</div>
				);
			}
			return (
				<TableData
					tableValue={props.getValue() || '-'}
					className="overflow-hidden text-ellipsis whitespace-nowrap px-3 w-[14rem]"
					title={props.getValue() as string}
				/>
			);
		},
	},
	{
		accessorKey: 'management.name',
		header: 'Gerência',
		meta: {
			headerClassName:
				'text-[#193CB9] bg-[#F5F7FF] pl-3 border-l border-[#EAEFFF]',
			getCellColSpan: subRowColSpan(0),
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				return null;
			}
			return (
				<TableData
					tableValue={props.getValue() || '-'}
					className="overflow-hidden text-ellipsis whitespace-nowrap px-3"
					title={props.getValue() as string}
				/>
			);
		},
	},
	{
		accessorKey: 'requesters',
		header: 'Solicitantes',
		meta: {
			headerClassName:
				'text-[#193CB9] bg-[#F5F7FF] pl-3 border-l border-[#EAEFFF]',
			getCellColSpan: subRowColSpan(0),
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				return null;
			}
			const requesters = props.getValue() as
				| Array<{ employeeId: number; name: string }>
				| undefined;
			const requestersText = requesters?.map((r) => r.name).join(', ') || '-';
			return (
				<TableData
					tableValue={requestersText}
					className="overflow-hidden text-ellipsis whitespace-nowrap px-3 w-[18rem]"
					title={requestersText}
				/>
			);
		},
	},
	{
		accessorKey: 'type',
		header: 'Tipo de fluxo',
		meta: {
			headerClassName:
				'text-[#193CB9] bg-[#F5F7FF] pl-3 border-l border-[#EAEFFF]',
			getCellColSpan: subRowColSpan(0),
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				return null;
			}
			const typeValue = props.getValue() as string;
			const typeLabel = typeValue ? getTypeLabel(typeValue) : '-';
			return (
				<TableData
					tableValue={typeLabel}
					className="overflow-hidden text-ellipsis whitespace-nowrap px-3 w-40"
					title={typeLabel}
				/>
			);
		},
	},
	{
		accessorKey: 'buttons',
		header: '',
		meta: {
			headerClassName: 'bg-[#F5F7FF]',
			getCellColSpan: subRowColSpan(0),
		},
		cell: (props) => {
			const isSubRow = (props.row.original as any).isSubRow;
			if (isSubRow) {
				return null;
			}
			return (
				<div className="w-20">
					<ApprovalFlowButtons
						approvalFlow={props.cell.row.original}
						onEdit={onEdit}
					/>
				</div>
			);
		},
		enableSorting: false,
	},
];

export const columns = getColumns();
